import { useState, useMemo, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Building2, Loader2, Settings, Trash2, Eye, EyeOff, MoreHorizontal, LayoutGrid, List } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useFleetSystems } from '@/hooks/useFleetSystems';
import { useFleetSparklineData } from '@/hooks/useFleetSparklineData';
import { usePropertyTags } from '@/hooks/usePropertyTags';
import { useSavedViews, useSaveView, useDeleteSavedView } from '@/hooks/useSavedViews';
import { FleetFilterBar } from '@/components/filters';
import { FleetSystemCard } from '@/components/fleet';
import {
  DEFAULT_FLEET_FILTER,
  applyFleetFilters,
  sortFleetSystems,
  hasActiveFilters,
  SAVED_VIEW_PAYLOAD_VERSION,
  type FleetFilterState,
  type FleetSortKey,
  type SavedViewPayload,
  type FilterableSystem,
  type PropertiesViewType,
} from '@/lib/filters';
import { cn } from '@/lib/utils';
import { t } from '@/lib/vocabulary';
import { PropertyTagsEditor } from '@/components/property/PropertyTagsEditor';
import { pruneRecentPropertyIds } from '@/lib/recent-properties';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronRight, MapPin } from 'lucide-react';
import { PropertiesMapView } from '@/components/properties';
import type { PropertyMapRow } from '@/components/properties';

const VIEW_TYPE_KEY = 'wt-properties-view-type';

/** Property + optional fleet system data + tags for display and filtering */
interface PropertyRow extends FilterableSystem {
  id: string;
  name: string;
  address: string | null;
  is_hidden?: boolean;
  battery_kwh_total: number;
  usable_fraction: number;
  baseline_load_w: number;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
  forecastRisk?: boolean;
  readiness_state?: string;
}

function gridStateLabel(gridState: FilterableSystem['gridState']): string {
  if (gridState == null) return '—';
  if (gridState === 'import') return 'Import';
  if (gridState === 'export') return 'Export';
  if (gridState === 'off-grid') return 'Off-grid';
  return '—';
}

function statusLabel(online: boolean, dataStale?: boolean, isHomeowner?: boolean): string {
  if (online && !dataStale) return t('live', isHomeowner ?? false);
  if (dataStale) return t('stale', isHomeowner ?? false);
  return t('offline', isHomeowner ?? false);
}

export default function Properties() {
  const {
    properties,
    loading: appLoading,
    refreshData,
    selectedPropertyId,
    selectProperty,
    showHiddenProperties,
    setShowHiddenProperties,
    isInstaller,
    isHomeowner,
    setPropertyScope,
  } = useApp();
  const navigate = useNavigate();
  const [propertyToRemove, setPropertyToRemove] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [hidingId, setHidingId] = useState<string | null>(null);
  /** Target for Edit tags modal (card view uses FleetSystem; list uses PropertyRow) */
  const [editingTagsTarget, setEditingTagsTarget] = useState<{ id: string; name: string; tags: string[] } | null>(null);

  const [viewType, setViewTypeState] = useState<PropertiesViewType>(() => {
    try {
      const s = localStorage.getItem(VIEW_TYPE_KEY);
      if (s === 'card' || s === 'list' || s === 'map') return s as PropertiesViewType;
    } catch {
      // ignore
    }
    return 'card';
  });
  const setViewType = (v: PropertiesViewType) => {
    setViewTypeState(v);
    try {
      localStorage.setItem(VIEW_TYPE_KEY, v);
    } catch {
      // ignore
    }
  };

  const [filterState, setFilterState] = useState<FleetFilterState>(DEFAULT_FLEET_FILTER);
  const [sortKey, setSortKey] = useState<FleetSortKey>('name');
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [viewToDelete, setViewToDelete] = useState<{ id: string; name: string } | null>(null);

  const { data: fleetData } = useFleetSystems();
  const { sparklineByPropertyId } = useFleetSparklineData();
  const systems = useMemo(() => fleetData?.systems ?? [], [fleetData?.systems]);
  const propertyIds = useMemo(() => properties.map((p) => p.id), [properties]);
  const { tagsByPropertyId } = usePropertyTags(propertyIds);

  // Card view: same filter/sort as Fleet dashboard; systems with tags/grid for FilterableSystem.
  const systemsWithFilterMeta = useMemo(
    () =>
      systems.map((s) => ({
        ...s,
        tags: tagsByPropertyId.get(s.id) ?? [],
        gridState: s.gridKw != null ? (s.gridKw >= 0 ? 'export' as const : 'import' as const) : null,
        propertyGridMode: s.gridMode ?? null,
      })),
    [systems, tagsByPropertyId]
  );
  const filteredForCardView = useMemo(
    () => applyFleetFilters(systemsWithFilterMeta, filterState),
    [systemsWithFilterMeta, filterState]
  );
  const sortedForCardView = useMemo(
    () => sortFleetSystems(filteredForCardView, sortKey, (s) => s.systemName),
    [filteredForCardView, sortKey]
  );
  const { views: savedViews, refetch: refetchViews } = useSavedViews();
  const saveView = useSaveView();
  const deleteView = useDeleteSavedView();

  // If the currently selected saved view was deleted (e.g. by another tab), fall back to default state without error.
  useEffect(() => {
    if (selectedViewId && savedViews.length >= 0) {
      const exists = savedViews.some((v) => v.id === selectedViewId);
      if (!exists) setSelectedViewId(null);
    }
  }, [selectedViewId, savedViews]);

  // Memoized: rows depend only on properties, systems, tagsByPropertyId (batched queries — no N+1).
  const rows: PropertyRow[] = useMemo(() => {
    const systemByPropId = new Map(systems.map((s) => [s.id, s]));
    return properties.map((p) => {
      const sys = systemByPropId.get(p.id);
      const tags = tagsByPropertyId.get(p.id) ?? [];
      return {
        id: p.id,
        name: p.name,
        address: p.address ?? null,
        is_hidden: p.is_hidden,
        battery_kwh_total: p.battery_kwh_total,
        usable_fraction: p.usable_fraction,
        baseline_load_w: p.baseline_load_w,
        timezone: p.timezone,
        latitude: p.latitude ?? null,
        longitude: p.longitude ?? null,
        online: sys?.online ?? false,
        dataStale: sys?.dataStale,
        forecastRisk: sys?.forecastRisk,
        provider: sys?.provider ?? 'unknown',
        socPct: sys?.socPct ?? 0,
        lastPollAt: sys?.lastPollAt ?? null,
        tags,
        gridState: sys?.gridKw != null ? (sys.gridKw >= 0 ? 'export' : 'import') : null,
        propertyGridMode: sys?.gridMode ?? null,
        readiness_state: p.readiness_state,
      };
    });
  }, [properties, systems, tagsByPropertyId]);

  // Memoized: filters and sort to avoid recomputing on every render.
  const filteredRows = useMemo(
    () => applyFleetFilters(rows, filterState),
    [rows, filterState]
  );
  const sortedRows = useMemo(
    () => sortFleetSystems(filteredRows, sortKey, (r) => r.name),
    [filteredRows, sortKey]
  );

  // Map view: same data as Cards/Grid; only rows with coordinates; count missing
  const mapRows: PropertyMapRow[] = useMemo(
    () =>
      sortedRows.filter(
        (r): r is PropertyRow & { latitude: number; longitude: number } =>
          r.latitude != null && r.longitude != null
      ) as PropertyMapRow[],
    [sortedRows]
  );
  const missingLocationCount = sortedRows.length - mapRows.length;

  const allTagNames = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.tags?.forEach((t) => set.add(t)));
    return [...set].sort();
  }, [rows]);

  const setPropertyHidden = async (propertyId: string, isHidden: boolean) => {
    setHidingId(propertyId);
    try {
      const { error } = await supabase
        .from('properties')
        .update({ is_hidden: isHidden })
        .eq('id', propertyId);
      if (error) throw error;
      toast.success(isHidden ? 'Property hidden' : 'Property visible again');
      await refreshData();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update visibility');
    } finally {
      setHidingId(null);
    }
  };

  const handleRemoveProperty = async () => {
    if (!propertyToRemove) return;
    setRemoving(true);
    try {
      const { error } = await supabase.from('properties').delete().eq('id', propertyToRemove.id);
      if (error) throw error;
      toast.success('Property removed', { description: `${propertyToRemove.name} has been removed.` });
      setPropertyToRemove(null);
      if (selectedPropertyId === propertyToRemove.id) {
        selectProperty(null);
        navigate('/app/properties', { replace: true });
      }
      await refreshData();
    } catch (e) {
      toast.error('Could not remove property', {
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setRemoving(false);
    }
  };

  const applySavedView = (viewId: string | null, payload: SavedViewPayload | null) => {
    if (!payload) return;
    setFilterState(payload.filters);
    setSortKey(payload.sortKey);
    // Map view is installer/fleet only; non-fleet users get list when saved view is map
    const viewTypeToApply =
      payload.viewType === 'map' && !isInstaller ? 'list' : payload.viewType;
    setViewType(viewTypeToApply);
    setSelectedViewId(viewId);
  };

  const handleDeleteSavedView = () => {
    if (!viewToDelete) return;
    deleteView.mutate(viewToDelete.id, {
      onSuccess: () => {
        if (selectedViewId === viewToDelete.id) setSelectedViewId(null);
        setViewToDelete(null);
        toast.success('View deleted');
        refetchViews();
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to delete view'),
    });
  };

  const handleSaveCurrentView = () => {
    const name = prompt('Name this view');
    if (!name?.trim()) return;
    const payload: SavedViewPayload = {
      version: SAVED_VIEW_PAYLOAD_VERSION,
      filters: filterState,
      sortKey,
      viewType,
    };
    saveView.mutate(
      { name: name.trim(), payload },
      {
        onSuccess: () => {
          toast.success('View saved');
          refetchViews();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to save view'),
      }
    );
  };

  const selectedView = savedViews.find((v) => v.id === selectedViewId);
  const hasUnsavedViewChanges =
    selectedView &&
    (JSON.stringify(filterState) !== JSON.stringify(selectedView.payload.filters) ||
      sortKey !== selectedView.payload.sortKey ||
      viewType !== selectedView.payload.viewType);

  // Recently viewed: prune to existing properties and clean localStorage; show at top (above filters).
  const validPropertyIds = useMemo(() => new Set(properties.map((p) => p.id)), [properties]);
  const recentPropertyIds = useMemo(() => {
    const pruned = pruneRecentPropertyIds(validPropertyIds);
    return pruned;
  }, [validPropertyIds]);
  const recentProperties = useMemo(
    () => recentPropertyIds.map((id) => properties.find((p) => p.id === id)).filter(Boolean) as { id: string; name: string }[],
    [recentPropertyIds, properties]
  );

  if (appLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Recently viewed: above filters; click enters property mode; pruned when properties no longer exist */}
      {recentProperties.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/50 bg-wt-bg-secondary/50 px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider shrink-0">
            Recently viewed
          </span>
          {recentProperties.map((p) => (
            <Button
              key={p.id}
              variant="secondary"
              size="sm"
              className="h-8 text-sm font-normal shrink-0"
              onClick={() => {
                setPropertyScope(p.id, p.name);
                navigate(`/app/property/${p.id}`);
              }}
            >
              {p.name}
            </Button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Fleet</h1>
          <p className="text-wt-text-secondary mt-1">Manage your fleet</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={showHiddenProperties}
              onChange={(e) => setShowHiddenProperties(e.target.checked)}
              className="rounded border-input"
            />
            Show hidden
          </label>
          <FleetFilterBar
            state={filterState}
            onChange={setFilterState}
            onClear={() => setFilterState(DEFAULT_FLEET_FILTER)}
            availableTags={allTagNames}
          />
          <Select value={sortKey} onValueChange={(v) => setSortKey(v as FleetSortKey)}>
            <SelectTrigger className="w-[130px] bg-wt-bg-secondary">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="status">Status</SelectItem>
              <SelectItem value="soc">Battery SOC</SelectItem>
              <SelectItem value="lastUpdate">Last update</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 border rounded-md p-1 bg-wt-bg-secondary">
            <Button
              variant={viewType === 'card' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewType('card')}
              title="Card view"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewType === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewType('list')}
              title="List view"
            >
              <List className="w-4 h-4" />
            </Button>
            {isInstaller && (
              <Button
                variant={viewType === 'map' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2"
                onClick={() => setViewType('map')}
                title="Map view"
              >
                <MapPin className="w-4 h-4" />
              </Button>
            )}
          </div>
          <Select
            value={selectedViewId ?? ''}
            onValueChange={(id) => {
              if (!id) return;
              const v = savedViews.find((x) => x.id === id);
              if (v?.payload) applySavedView(v.id, v.payload);
            }}
          >
            <SelectTrigger className={cn('w-[160px] bg-wt-bg-secondary', hasUnsavedViewChanges && 'ring-1 ring-amber-500/50')}>
              <SelectValue placeholder="Saved views" />
            </SelectTrigger>
            <SelectContent>
              {savedViews.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name}
                </SelectItem>
              ))}
              {savedViews.length === 0 && (
                <SelectItem value="_none" disabled>
                  No saved views
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          {hasUnsavedViewChanges && (
            <span className="text-xs text-muted-foreground" title="Current state differs from saved view">
              (unsaved)
            </span>
          )}
          {selectedViewId && selectedView && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => setViewToDelete({ id: selectedView.id, name: selectedView.name })}
            >
              Delete view
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleSaveCurrentView} disabled={saveView.isPending}>
            Save current view
          </Button>
          <Button onClick={() => navigate('/app/onboarding')}>
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </div>
      </div>

      {properties.length === 0 ? (
        <Card className="bg-wt-bg-secondary border-border/50">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-wt-text-secondary mb-4" />
            <h3 className="text-lg font-medium mb-2">No sites in your fleet yet</h3>
            <p className="text-wt-text-secondary mb-4">
              Get started by adding your first property. Run onboarding to add systems.
            </p>
            <Button onClick={() => navigate('/app/onboarding')}>
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </CardContent>
        </Card>
      ) : viewType === 'map' && isInstaller ? (
        <PropertiesMapView
          rows={mapRows}
          missingLocationCount={missingLocationCount}
        />
      ) : viewType === 'card' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sortedForCardView.map((system) => (
            <FleetSystemCard
              key={system.id}
              system={system}
              sparklineData={sparklineByPropertyId.get(system.id)}
              onHide={(propertyId) => setPropertyHidden(propertyId, true)}
              onRemove={(id, name) => setPropertyToRemove({ id, name })}
              onSettingsClick={() => navigate(`/app/property/${system.id}/settings`)}
              onEditTags={() =>
                setEditingTagsTarget({
                  id: system.id,
                  name: system.systemName,
                  tags: tagsByPropertyId.get(system.id) ?? [],
                })
              }
            />
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-border bg-wt-bg-secondary">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>PV kW</TableHead>
                <TableHead>Battery SOC</TableHead>
                <TableHead>Load kW</TableHead>
                <TableHead>Grid</TableHead>
                <TableHead>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">Last update</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      Time since last successful poll. &lt;15 min = online, 15–30 min = stale, &gt;30 min = offline.
                    </TooltipContent>
                  </Tooltip>
                </TableHead>
                <TableHead>Tags</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row) => {
                const sys = systems.find((s) => s.id === row.id);
                const pvKw = sys?.pvKw ?? 0;
                const loadKw = sys?.loadKw ?? 0;
                const isStale = row.dataStale || (!row.online && row.lastPollAt != null);
                const lastUpdateText = row.lastPollAt
                  ? formatDistanceToNow(new Date(row.lastPollAt), { addSuffix: true })
                  : '—';
                return (
                  <TableRow
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    className={cn(
                      'cursor-pointer transition-colors hover:bg-muted/70 group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset'
                    )}
                    onClick={() => {
                      setPropertyScope(row.id, row.name);
                      navigate(`/app/property/${row.id}`);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setPropertyScope(row.id, row.name);
                        navigate(`/app/property/${row.id}`);
                      }
                    }}
                  >
                    <TableCell>
                      <div className="font-medium truncate max-w-[200px]">{row.name}</div>
                      {row.address && (
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {row.address}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.readiness_state === 'provisional' ? (
                        <Badge variant="outline" className="border-wt-slate-blue/50 text-wt-slate-blue">
                          Pending Setup
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className={cn(
                            row.online && !row.dataStale && 'border-status-online/50 text-status-online',
                            row.dataStale && 'border-status-stale/50 text-status-stale',
                            !row.online && 'border-status-offline/50 text-status-offline'
                          )}
                        >
                          {statusLabel(row.online, row.dataStale, isHomeowner)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-numeric text-sm">
                      {sys ? `${pvKw.toFixed(1)}` : '—'}
                    </TableCell>
                    <TableCell className="font-mono-numeric text-sm">{row.socPct}%</TableCell>
                    <TableCell className="font-mono-numeric text-sm">
                      {sys ? `${loadKw.toFixed(1)}` : '—'}
                    </TableCell>
                    <TableCell className="text-sm">{gridStateLabel(row.gridState)}</TableCell>
                    <TableCell className={cn('text-sm', isStale && 'text-muted-foreground')}>
                      {lastUpdateText}
                      {isStale && row.lastPollAt && (
                        <span className="ml-1.5 text-xs text-status-stale" title={isHomeowner ? 'Data is outdated' : 'Data is stale (>15 min since last poll)'}>
                          ({t('stale', isHomeowner).toLowerCase()})
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(row.tags ?? []).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs font-normal">
                            {tag}
                          </Badge>
                        ))}
                        {(!row.tags || row.tags.length === 0) && '—'}
                      </div>
                    </TableCell>
                    <TableCell className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ChevronRight className="w-4 h-4 text-muted-foreground" aria-hidden />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {(viewType === 'card' ? sortedForCardView.length : sortedRows.length) === 0 && properties.length > 0 && (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
          <p className="text-sm text-wt-text-secondary">
            No sites match these filters.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilterState(DEFAULT_FLEET_FILTER)}
          >
            Clear filters
          </Button>
        </div>
      )}

      {editingTagsTarget && (
        <PropertyTagsEditor
          propertyId={editingTagsTarget.id}
          propertyName={editingTagsTarget.name}
          initialTags={editingTagsTarget.tags}
          open={true}
          onOpenChange={(o) => !o && setEditingTagsTarget(null)}
          onSaved={() => setEditingTagsTarget(null)}
        />
      )}

      <AlertDialog open={!!propertyToRemove} onOpenChange={(open) => !open && setPropertyToRemove(null)}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove property?</AlertDialogTitle>
            <AlertDialogDescription>
              {propertyToRemove && (
                <>
                  This will remove <strong>{propertyToRemove.name}</strong> and all its plants and equipment from Watt Tower.
                  Historical data for this property will no longer be visible. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleRemoveProperty();
              }}
              disabled={removing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing…
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!viewToDelete} onOpenChange={(open) => !open && setViewToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete saved view?</AlertDialogTitle>
            <AlertDialogDescription>
              {viewToDelete && (
                <>
                  &quot;{viewToDelete.name}&quot; will be removed. You can save the current state again later.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteView.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteSavedView();
              }}
              disabled={deleteView.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteView.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
